// ----------------------------------------------------------------------
// "Why choose us" section. Mirrors AboutWidget (text left, collage right) so
// the two split sections read as a deliberate pair rather than a repeat.

import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Box, Grid, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInLeft, varFadeInRight } from '../../../components/animate';
import { SectionHeader, SectionShell } from '../common';

// ----------------------------------------------------------------------

const LIST_IMG = [
  { id: 1, url: '/assets/images/asap/about_01.jpg', alt: 'Classroom training in progress' },
  { id: 2, url: '/assets/images/asap/about_02.jpg', alt: 'Mentor supporting a student' },
];

const LIST_FEATURES = [
  { id: 1, icon: 'lucide:user-check', body: 'Well experienced trainers.' },
  { id: 2, icon: 'lucide:briefcase', body: 'Practical tips to help crack job interviews.' },
  { id: 3, icon: 'lucide:book-open', body: 'Project implementation guidance and Course Materials.' },
  { id: 4, icon: 'lucide:clock', body: 'Moderate fees and flexible timing.' },
  { id: 5, icon: 'lucide:laptop', body: 'Online classes available.' },
];

// ----------------------------------------------------------------------

const AccentRail = styled('span')(({ theme }) => ({
  display: { xs: 'none', md: 'block' },
  position: 'absolute',
  top: theme.spacing(4),
  insetInlineEnd: { md: -theme.spacing(3), lg: -theme.spacing(5) },
  width: theme.spacing(3),
  height: '82%',
  borderStartEndRadius: 15,
  borderEndEndRadius: 15,
  backgroundColor: '#DF3E30',
  transition: 'background-color 0.4s ease',
  '@media (hover: hover)': {
    '.benefit-collage:hover &': { backgroundColor: '#1877F2' },
  },
}));

// ----------------------------------------------------------------------

function FeatureGrid({ items }) {
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        p: 0,
        listStyle: 'none',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
        gap: { xs: 1.25, sm: 1.5 },
      }}
    >
      {items.map((item, index) => (
        <Box
          component={motion.li}
          key={item.id}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: index * 0.08, ease: 'easeOut' }}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            height: '100%',
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            transition: 'transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateY(-3px)',
                borderColor: alpha('#DF3E30', 0.4),
                boxShadow: (theme) => `0 12px 24px ${alpha(theme.palette.grey[900], 0.1)}`,
                '& .benefit-icon': {
                  transform: 'scale(1.08)',
                  bgcolor: alpha('#DF3E30', 0.16),
                },
              },
            },
          }}
        >
          <Box
            className="benefit-icon"
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              color: '#DF3E30',
              bgcolor: alpha('#DF3E30', 0.08),
              transition: 'transform 0.3s ease, background-color 0.3s ease',
            }}
          >
            <Iconify icon={item.icon} width={18} />
          </Box>

          <Typography variant="subtitle2" sx={{ color: 'text.secondary', pt: 0.75, lineHeight: 1.6 }}>
            {item.body}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

FeatureGrid.propTypes = { items: PropTypes.arrayOf(PropTypes.object).isRequired };

// ----------------------------------------------------------------------

function ImageCollage({ images, ratio = '4 / 3' }) {
  return (
    <Box
      className="benefit-collage"
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: { xs: 1, sm: 1.5 },
        px: { xs: 0, md: 2 },
        py: { xs: 1, md: 2 },
        borderRadius: 3,
        backgroundImage: () =>
          `radial-gradient(58% 52% at 12% 10%, ${alpha('#1877F2', 0.12)} 0%, transparent 62%), radial-gradient(55% 50% at 92% 92%, ${alpha('#DF3E30', 0.12)} 0%, transparent 60%)`,
      }}
    >
      <AccentRail />
      {images.map((image, index) => (
        <MotionInView
          key={image.id}
          variants={varFadeInRight}
          transition={{ delay: 0.15 + index * 0.12 }}
          sx={{
            mt: index % 2 === 1 ? { xs: 3, md: 5 } : 0,
            position: 'relative',
            borderRadius: '15px',
            overflow: 'hidden',
            aspectRatio: ratio,
            backgroundColor: (t) => alpha(t.palette.grey[500], 0.12),
          }}
        >
          <Box
            component="img"
            src={image.url}
            alt={image.alt}
            loading="lazy"
            decoding="async"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              transition: 'transform 0.6s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
              '@media (hover: hover)': {
                '.benefit-collage:hover &': { transform: 'scale(1.06)' },
              },
            }}
          />
        </MotionInView>
      ))}
    </Box>
  );
}

ImageCollage.propTypes = { images: PropTypes.arrayOf(PropTypes.object).isRequired, ratio: PropTypes.string };

// ----------------------------------------------------------------------

export default function BenfitWidget() {
  return (
    <SectionShell>
      <Grid container spacing={{ xs: 4, md: 6 }} sx={{ alignItems: 'center' }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <MotionInView variants={varFadeInRight}>
            <SectionHeader
              align="left"
              titleVariant="h3"
              eyebrow="Why Choose Us?"
              title="Benefits of Learning from asap marketing and studies consultancy."
              description="ASAP Marketing and Studies Consultancy has mentored thousands of professionals in varied fields, helped them reach their goals and provided outstanding career support to these professionals. Apart from quality education and training, we provide the following facilities:"
              sx={{ mb: { xs: 2.5, md: 3 } }}
            />
          </MotionInView>

          <FeatureGrid items={LIST_FEATURES} />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <MotionInView variants={varFadeInLeft}>
            <ImageCollage images={LIST_IMG} />
          </MotionInView>
        </Grid>
      </Grid>
    </SectionShell>
  );
}
